import React from 'react';
import {
    Mic, MicOff, Video, VideoOff, Monitor, MonitorOff, PhoneOff,
    Send, MessageSquare, Clock, Users,
} from 'lucide-react';
import AudioEqualizerBars from './AudioEqualizerBars';

// Sala de videollamada activa de una sesión pública de terminal: escenario principal +
// controles + carrusel de participantes + panel de comentarios/chat. Presentacional — toda la
// lógica vive en useTerminalSession. Extraído de PublicSessionView (integración main→dev, B1).
const SessionRoom = ({
    space,
    terminalId,
    sessionId,
    participantName,
    authUser,
    peers,
    localStream,
    remoteStreams,
    selectedSpeakerId,
    setSelectedSpeakerId,
    micEnabled,
    camEnabled,
    screenSharing,
    timeRemaining,
    chatMessages,
    inputMessage,
    setInputMessage,
    mainVideoRef,
    chatEndRef,
    guestTokenRef,
    onToggleMic,
    onToggleCam,
    onToggleScreenShare,
    onSendMessage,
    onLeave,
}) => {
    const allParticipants = [
        {
            id: 'local',
            name: `${participantName || 'Tú'} (Tú)`,
            isLocal: true,
            audio: micEnabled,
            video: camEnabled,
            image: authUser?.image || null,
        },
        ...peers,
    ];

    const activeSpeaker = allParticipants.find((p) => p.id === selectedSpeakerId) || allParticipants[0];
    const activeStream = activeSpeaker.id === 'local' ? localStream : remoteStreams[activeSpeaker.id];
    const hasActiveVideo = activeSpeaker.id === 'local' ? camEnabled : (activeSpeaker.video && !!activeStream);

    return (
        <div className="psv-container">
            <header className="psv-header">
                <div className="psv-header-left">
                    <div className="psv-brand">
                        <span style={{ color: '#3b82f6' }}>●</span>
                        <span>Colonny</span>
                    </div>
                    <div className="psv-address-bar">
                        <span className="psv-address-path">/{space || 'all'}/terminal {terminalId || '101'}</span>
                        <span className="psv-address-sep">/</span>
                        <span className="psv-address-id">#{sessionId}</span>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="psv-live-indicator">
                        <span className="psv-live-dot" />
                        <span>En vivo</span>
                    </div>

                    {timeRemaining && (
                        <div className="psv-timer" title="Tiempo restante de la cita en calendario">
                            <Clock size={13} />
                            <span>{timeRemaining}</span>
                        </div>
                    )}

                    <div className="psv-timer" style={{ color: '#93c5fd', background: 'rgba(59, 130, 246, 0.1)', borderColor: 'rgba(59, 130, 246, 0.25)' }}>
                        <Users size={13} />
                        <span>{allParticipants.length} en sala</span>
                    </div>
                </div>
            </header>

            <main className="psv-body">
                <div className="psv-video-column">
                    <div className="psv-square-box">
                        <div className="psv-main-stage">
                            {hasActiveVideo ? (
                                <video
                                    ref={(el) => {
                                        mainVideoRef.current = el;
                                        if (el && activeStream && el.srcObject !== activeStream) {
                                            el.srcObject = activeStream;
                                        }
                                    }}
                                    autoPlay
                                    playsInline
                                    muted
                                    className={`psv-main-video ${activeSpeaker.isLocal ? 'mirror' : ''}`}
                                />
                            ) : (
                                <div className="psv-avatar-placeholder">
                                    <div
                                        className="psv-avatar-circle"
                                        style={!activeSpeaker.isLocal ? { background: 'linear-gradient(135deg, #059669, #10b981)' } : undefined}
                                    >
                                        {activeSpeaker.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ fontSize: '15px', color: '#9ca3af' }}>
                                        {activeSpeaker.isLocal ? 'Cámara desactivada' : activeSpeaker.name}
                                    </div>
                                </div>
                            )}

                            <div className="psv-stage-info">
                                <span>{activeSpeaker.name}</span>
                                {!activeSpeaker.audio ? (
                                    <MicOff size={14} color="#ef4444" />
                                ) : (
                                    <div style={{ marginLeft: '4px', display: 'flex', alignItems: 'center' }}>
                                        <AudioEqualizerBars stream={activeStream} isMuted={false} color="#22c55e" numBars={7} />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="psv-controls-bar">
                            <button
                                type="button"
                                className={`psv-btn-control ${micEnabled ? 'active' : 'muted'}`}
                                onClick={onToggleMic}
                                title={micEnabled ? 'Silenciar micrófono' : 'Activar micrófono'}
                            >
                                {micEnabled ? <Mic size={20} /> : <MicOff size={20} />}
                            </button>

                            <button
                                type="button"
                                className={`psv-btn-control ${camEnabled ? 'active' : 'muted'}`}
                                onClick={onToggleCam}
                                title={camEnabled ? 'Apagar cámara' : 'Encender cámara'}
                            >
                                {camEnabled ? <Video size={20} /> : <VideoOff size={20} />}
                            </button>

                            <button
                                type="button"
                                className={`psv-btn-control ${screenSharing ? 'muted' : 'active'}`}
                                onClick={onToggleScreenShare}
                                title={screenSharing ? 'Dejar de compartir pantalla' : 'Compartir pantalla'}
                            >
                                {screenSharing ? <MonitorOff size={20} /> : <Monitor size={20} />}
                            </button>

                            <button
                                type="button"
                                className="psv-btn-control leave"
                                onClick={onLeave}
                                title="Salir de la reunión"
                            >
                                <PhoneOff size={20} />
                            </button>
                        </div>

                        <div className="psv-participants-tray">
                            {allParticipants.map((p) => {
                                const isSelected = p.id === selectedSpeakerId;
                                const pStream = p.isLocal ? localStream : remoteStreams[p.id];
                                const pHasVideo = p.isLocal ? camEnabled : (p.video && !!pStream);

                                return (
                                    <div
                                        key={p.id}
                                        className={`psv-participant-card ${isSelected ? 'selected' : ''}`}
                                        onClick={() => setSelectedSpeakerId(p.id)}
                                        title={`Ver a ${p.name}`}
                                    >
                                        {pHasVideo ? (
                                            <video
                                                ref={(el) => {
                                                    if (el && pStream && el.srcObject !== pStream) {
                                                        el.srcObject = pStream;
                                                    }
                                                }}
                                                autoPlay
                                                playsInline
                                                muted
                                                className={`psv-card-video ${p.isLocal ? 'mirror' : ''}`}
                                            />
                                        ) : (
                                            <div className="psv-card-avatar" style={!p.isLocal ? { background: '#059669' } : undefined}>
                                                {p.image ? (
                                                    <img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                                ) : (
                                                    p.name.charAt(0).toUpperCase()
                                                )}
                                            </div>
                                        )}

                                        <div className="psv-card-name">{p.name}</div>

                                        <div className="psv-card-mic-status">
                                            {p.audio ? <Mic size={10} color="#22c55e" /> : <MicOff size={10} color="#ef4444" />}
                                        </div>

                                        <div style={{ position: 'absolute', bottom: 18, left: 0, right: 0, zIndex: 4, pointerEvents: 'none' }}>
                                            <AudioEqualizerBars stream={pStream} isMuted={!p.audio} color="#22c55e" numBars={5} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="psv-chat-column">
                    <div className="psv-chat-header">
                        <h2 className="psv-chat-title">
                            <MessageSquare size={18} color="#3b82f6" />
                            <span>Comentarios &amp; Notas</span>
                        </h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '10px', color: '#10b981', background: 'rgba(16, 185, 129, 0.1)', padding: '2px 6px', border: '1px solid rgba(16, 185, 129, 0.25)', borderRadius: '4px', fontWeight: 600 }}>
                                En Calendario
                            </span>
                            <span className="psv-chat-count">
                                {chatMessages.filter((m) => !m.system).length}
                            </span>
                        </div>
                    </div>

                    <div className="psv-chat-messages">
                        {chatMessages.length === 0 ? (
                            <div className="psv-empty-chat">
                                <MessageSquare size={32} style={{ opacity: 0.3 }} />
                                <div>No hay comentarios aún.</div>
                                <div style={{ fontSize: '11px', color: '#4b5563' }}>
                                    Escribe aquí notas y mensajes durante la videollamada. Quedarán registrados en el evento del calendario.
                                </div>
                            </div>
                        ) : (
                            chatMessages.map((msg) => {
                                if (msg.system) {
                                    return (
                                        <div key={msg.id} style={{ fontSize: '11px', color: '#6b7280', textAlign: 'center', margin: '4px 0' }}>
                                            {msg.text}
                                        </div>
                                    );
                                }

                                const isMine = msg.senderId === (guestTokenRef.current || 'guest') || msg.senderName === participantName;
                                return (
                                    <div key={msg.id} className={`psv-chat-bubble ${isMine ? 'mine' : ''}`}>
                                        <div className="psv-chat-bubble-avatar">
                                            {msg.senderName ? msg.senderName.charAt(0).toUpperCase() : 'U'}
                                        </div>
                                        <div className="psv-chat-bubble-content">
                                            {!isMine && <div className="psv-chat-sender">{msg.senderName}</div>}
                                            <div className="psv-chat-text">{msg.text}</div>
                                            <div className="psv-chat-time">
                                                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                        <div ref={chatEndRef} />
                    </div>

                    <form className="psv-chat-input-bar" onSubmit={onSendMessage}>
                        <input
                            type="text"
                            className="psv-chat-input"
                            placeholder="Escribe un comentario o nota..."
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                        />
                        <button
                            type="submit"
                            className="psv-btn-send"
                            disabled={!inputMessage.trim()}
                            title="Enviar comentario"
                        >
                            <Send size={16} />
                        </button>
                    </form>
                </div>
            </main>
        </div>
    );
};

export default SessionRoom;
